import React, { useEffect, useState } from "react";
import { Plus, X, ChevronsUp } from "lucide-react";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const EMPTY = {
  priority: "P1", condition: "not_picked_up", threshold_mins: 30,
  action: "reassign_l2", notify_role: "manager", auto_close_pending_days: 0,
};

export default function AutoEscalationRules() {
  const [rules,    setRules]    = useState([]);
  const [draft,    setDraft]    = useState(EMPTY);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [msg,      setMsg]      = useState(null);

  // Global auto-close config
  const [autoClose, setAutoClose] = useState({ enabled: false, pending_days: 7, notify_before_days: 1 });
  const [savingAC,  setSavingAC]  = useState(false);

  const token = localStorage.getItem("token");
  const authH = () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" });

  const load = async () => {
    setLoading(true);
    try {
      const [r, ac] = await Promise.all([
        fetch(`${API}/api/admin/tickets/auto-escalation`, { headers: authH() }).then(x => x.json()),
        fetch(`${API}/api/admin/tickets/auto-close`,      { headers: authH() }).then(x => x.json()),
      ]);
      setRules(Array.isArray(r) ? r : []);
      if (ac && typeof ac === "object") setAutoClose(a => ({ ...a, ...ac }));
    } catch { setRules([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(`${API}/api/admin/tickets/auto-escalation`, {
        method: "POST", headers: authH(), body: JSON.stringify(draft),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDraft(EMPTY); setShowForm(false); setMsg({ type: "success", text: "✓ Rule saved." });
      await load();
    } catch (e) { setMsg({ type: "error", text: `⚠ ${e.message}` }); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    await fetch(`${API}/api/admin/tickets/auto-escalation/${id}`, { method: "DELETE", headers: authH() });
    setRules(r => r.filter(x => x.id !== id));
  };

  const saveAutoClose = async () => {
    setSavingAC(true);
    try {
      await fetch(`${API}/api/admin/tickets/auto-close`, {
        method: "PUT", headers: authH(), body: JSON.stringify(autoClose),
      });
      setMsg({ type: "success", text: "✓ Auto-close settings saved." });
    } catch (e) { setMsg({ type: "error", text: `⚠ ${e.message}` }); }
    finally { setSavingAC(false); }
  };

  const CONDITIONS = [
    { value: "not_picked_up",     label: "Not picked up within threshold" },
    { value: "no_reply",          label: "No reply from agent within threshold" },
    { value: "pending_too_long",  label: "In Pending for too long" },
    { value: "sla_about_to_breach",label: "SLA about to breach (30 min warning)" },
  ];

  const ACTIONS = [
    { value: "reassign_l2",      label: "Reassign to L2 queue"        },
    { value: "notify_manager",   label: "Notify manager"              },
    { value: "change_priority",  label: "Upgrade priority by 1 level" },
    { value: "add_to_p1_queue",  label: "Move to P1 queue"            },
  ];

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ChevronsUp size={13} className="text-[#00ff90]/60" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Auto-Escalation Rules</span>
        </div>
        <button onClick={() => setShowForm(v => !v)}
          className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
          <Plus size={12} /> Add Rule
        </button>
      </div>

      {showForm && (
        <div className="px-5 py-4 border-b border-white/5 bg-[#00ff90]/3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Priority</label>
              <select className={inputCls} value={draft.priority} onChange={e => setDraft(d => ({...d, priority: e.target.value}))}>
                {["P1","P2","P3","P4","ALL"].map(p => <option key={p}>{p}</option>)}
              </select></div>
            <div><label className={labelCls}>Threshold (minutes)</label>
              <input type="number" className={inputCls} value={draft.threshold_mins} onChange={e => setDraft(d => ({...d, threshold_mins: Number(e.target.value)}))} /></div>
            <div><label className={labelCls}>Condition</label>
              <select className={inputCls} value={draft.condition} onChange={e => setDraft(d => ({...d, condition: e.target.value}))}>
                {CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select></div>
            <div><label className={labelCls}>Action</label>
              <select className={inputCls} value={draft.action} onChange={e => setDraft(d => ({...d, action: e.target.value}))}>
                {ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select></div>
            <div><label className={labelCls}>Notify Role</label>
              <input className={inputCls} value={draft.notify_role || ""} onChange={e => setDraft(d => ({...d, notify_role: e.target.value}))} placeholder="manager, hr_head…" /></div>
          </div>
          {msg && <p className={`text-xs ${msg.type === "error" ? "text-red-400" : "text-[#00ff90]"}`}>{msg.text}</p>}
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowForm(false)} className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
            <button onClick={save} disabled={saving}
              className="text-xs px-4 py-1.5 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors">
              {saving ? "Saving…" : "Save Rule"}
            </button>
          </div>
        </div>
      )}

      <div className="divide-y divide-white/5 max-h-56 overflow-y-auto">
        {loading ? <div className="p-4"><div className="h-8 rounded-lg bg-white/5 animate-pulse" /></div>
        : rules.length === 0 ? <p className="px-5 py-4 text-white/20 text-xs italic text-center">No auto-escalation rules configured.</p>
        : rules.map(r => (
          <div key={r.id} className="flex items-center justify-between px-5 py-3 gap-3">
            <div className="min-w-0">
              <p className="text-xs text-white/60">
                <span className="text-white/40 font-medium">{r.priority}</span>
                <span className="text-white/25"> · </span>
                <span>{CONDITIONS.find(c => c.value === r.condition)?.label || r.condition}</span>
                <span className="text-white/25"> · </span>
                <span className="text-[#00ff90]/50">{r.threshold_mins}min</span>
              </p>
              <p className="text-xs text-white/30">{ACTIONS.find(a => a.value === r.action)?.label || r.action} · notify: {r.notify_role || "—"}</p>
            </div>
            <button onClick={() => del(r.id)} className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors shrink-0"><X size={12} /></button>
          </div>
        ))}
      </div>

      {/* Auto-close config */}
      <div className="px-5 py-4 border-t border-white/5 space-y-3">
        <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider">Auto-Close Pending Tickets</p>
        <label className="flex items-center gap-2 cursor-pointer text-sm text-white/50">
          <input type="checkbox" checked={!!autoClose.enabled} onChange={e => setAutoClose(a => ({...a, enabled: e.target.checked}))} className="w-4 h-4 accent-[#00ff90]" />
          Auto-close tickets in "Pending" with no reply
        </label>
        {autoClose.enabled && (
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Close after (days)</label>
              <input type="number" className={inputCls} value={autoClose.pending_days} onChange={e => setAutoClose(a => ({...a, pending_days: Number(e.target.value)}))} /></div>
            <div><label className={labelCls}>Warn requester (days before)</label>
              <input type="number" className={inputCls} value={autoClose.notify_before_days} onChange={e => setAutoClose(a => ({...a, notify_before_days: Number(e.target.value)}))} /></div>
          </div>
        )}
        {msg && <p className={`text-xs ${msg.type === "error" ? "text-red-400" : "text-[#00ff90]"}`}>{msg.text}</p>}
        <button onClick={saveAutoClose} disabled={savingAC}
          className="text-xs px-4 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors">
          {savingAC ? "Saving…" : "Save Auto-Close Settings"}
        </button>
      </div>
    </div>
  );
}